
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LandingHeader } from '../../components/layouts/LandingHeader';
import { useAuth } from '../../contexts/AuthContext';

export function LoginPage() {
    const { login, logout, isAuthenticated, isLoading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false,
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showLoginForm, setShowLoginForm] = useState(!isAuthenticated);

    // Only auto-redirect if coming from a protected route (has location.state.from)
    useEffect(() => {
        if (isAuthenticated && !isLoading && location.state?.from) {
            const from = location.state.from.pathname || getDefaultRedirect();
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate, location]);

    // After a fresh login (showLoginForm was true, now authenticated), redirect
    useEffect(() => {
        if (isAuthenticated && !isLoading && showLoginForm) {
            navigate(getDefaultRedirect(), { replace: true });
        }
    }, [isAuthenticated, isLoading, showLoginForm]);

    const getDefaultRedirect = () => {
        // Determine redirect based on user role
        if (!user) return '/company/dashboard';

        switch (user.role) {
            case 'COMPANY_ADMIN':
            case 'COMPANY_STAFF':
                return '/company/dashboard';
            case 'CONSULTANT':
            case 'CONSULTANT_ADMIN':
                return '/consultant/dashboard';
            case 'AUDITOR':
            case 'LEAD_AUDITOR':
                return '/auditor/dashboard';
            case 'SYSTEM_ADMIN':
            case 'CB_ADMIN':
                return '/admin/dashboard';
            default:
                return '/company/dashboard';
        }
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            await login(formData.email, formData.password, formData.rememberMe);
            // Redirect will happen via useEffect when isAuthenticated becomes true
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
        );
    }

    // If authenticated and not coming from a redirect, show session options
    if (isAuthenticated && !isLoading && !showLoginForm) {
        return (
            <div className="min-h-screen bg-gray-50">
                <LandingHeader />
                <div className="flex items-center justify-center pt-20 px-4">
                    <div className="w-full max-w-md">
                        <div className="bg-white rounded-lg shadow-md p-8">
                            <div className="text-center mb-6">
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Signed In</h1>
                                <p className="text-gray-600">
                                    Signed in as <strong>{user?.fullName || user?.email}</strong>
                                </p>
                                <p className="text-sm text-gray-500 mt-1">Role: {user?.role}</p>
                            </div>
                            <div className="space-y-3">
                                <button
                                    onClick={() => navigate(getDefaultRedirect())}
                                    className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Go to Dashboard
                                </button>
                                <button
                                    onClick={async () => {
                                        await logout();
                                        setShowLoginForm(true);
                                    }}
                                    className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Sign in as different user
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <LandingHeader />
            <div className="flex items-center justify-center pt-20 px-4">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign In</h1>
                            <p className="text-gray-600">Access your HALMAS account</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                                    placeholder="Enter your email"
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                                    placeholder="Enter your password"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="rememberMe"
                                    name="rememberMe"
                                    type="checkbox"
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                />
                                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                                    Remember me
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Signing in...
                                    </div>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </form>

                        <div className="mt-6 text-center">
                            <div className="text-sm text-gray-600 mb-4">
                                Demo Credentials:
                                <br />
                                <strong>Company Admin:</strong> admin@kantinwarisan.com / password123
                                <br />
                                <strong>Consultant:</strong> ahmad@consultant.com / password123
                                <br />
                                <strong>Auditor:</strong> mohamed@jais.gov.my / password123
                                <br />
                                <strong>System Admin:</strong> admin@halmas.gov.my / password123
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
